import { logger } from '../utils/logger';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp,
  query,
  orderBy,
  getDoc
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { EventItem } from '../types/event';
import { getAuth } from 'firebase/auth';
import { checkAdminClaims } from '../utils/adminClaims';
import { validateEventDate } from '../utils/dateUtils';

// Collection reference name
const EVENTS_COLLECTION = 'events';

/**
 * Fetch all events from Firestore
 * @returns A promise that resolves to an array of EventItem objects
 */
export const getEvents = async (): Promise<EventItem[]> => {
  logger.info('📅 Fetching events list from Firestore');

  try {
    const db = getFirestore();
    const eventsRef = collection(db, EVENTS_COLLECTION);
    const eventsQuery = query(eventsRef, orderBy('date', 'desc'));
    
    const snapshot = await getDocs(eventsQuery);
    
    // Map Firestore documents to EventItem objects
    const events = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title || '',
        date: data.date || '',
        imageUrl: data.imageUrl || '',
        address: data.address || '',
        description: data.description || '',
        startTime: {
          doorsOpen: data.startTime?.doorsOpen || '00:00',
          doorsClose: data.startTime?.doorsClose || '00:00',
        },
        endTime: data.endTime || null,
      } as EventItem;
    });
    
    logger.success('✅ Retrieved events successfully', { count: events.length });
    return events;
  } catch (error) {
    logger.error('❌ Error fetching events:', error);
    throw new Error('Failed to fetch events');
  }
};

/**
 * Create a new event in Firestore with an optional image upload
 * @param event The event data to create (without ID)
 * @param imageFile Optional image file to upload
 * @returns A promise that resolves to the created EventItem
 */
export const createEvent = async (
  event: Omit<EventItem, 'id'>, 
  imageFile: File | null
): Promise<EventItem> => {
  logger.info('🆕 Creating new event in Firestore', { title: event.title, date: event.date });
  
  try {
    // Check if the current user is logged in and has admin rights
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to create events');
    }
    
    // Check if user has admin role
    const isAdmin = await checkAdminClaims(currentUser);
    if (!isAdmin) {
      throw new Error('Only administrators can create events');
    }
    
    // Validate event date is not in the past
    const dateValidationError = validateEventDate(event.date);
    if (dateValidationError) {
      logger.error('❌ Event date validation failed', { date: event.date, error: dateValidationError });
      throw new Error(dateValidationError);
    }
    
    // Upload image if provided
    let imageUrl = '';
    if (imageFile) {
      imageUrl = await uploadEventImage(imageFile);
    }
    
    // Add event to Firestore
    const db = getFirestore();
    const eventsRef = collection(db, EVENTS_COLLECTION);
    
    const eventData = {
      ...event,
      imageUrl: imageUrl || event.imageUrl,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(eventsRef, eventData);
    
    // Get ID from created document
    const createdEvent: EventItem = {
      ...event,
      id: docRef.id,
      imageUrl: imageUrl || event.imageUrl
    };
    
    logger.success('✅ Event created successfully', { id: createdEvent.id });
    return createdEvent;
  } catch (error) {
    logger.error('❌ Error creating event:', error);
    
    // If the error comes from the image upload function, it will already have a descriptive message
    if (error instanceof Error) {
      throw error;
    }
    
    throw new Error('Failed to create event. Please check your connection and permissions.');
  }
};

/**
 * Update an existing event in Firebase Firestore
 * @param id The ID of the event to update
 * @param eventData The updated event data
 * @param imageFile Optional new image file for the event
 * @returns A promise that resolves to the updated EventItem
 */
export const updateEvent = async (
  id: string,
  eventData: Omit<EventItem, 'id'>,
  imageFile: File | null
): Promise<EventItem> => {
  logger.info('🔄 Updating event in Firestore', { id });
  
  try {
    // Check if the current user is logged in and has admin rights
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to update events');
    }
    
    // Check if user has admin role
    const isAdmin = await checkAdminClaims(currentUser);
    if (!isAdmin) {
      throw new Error('Only administrators can update events');
    }
    
    // Validate event date is not in the past
    const dateValidationError = validateEventDate(eventData.date);
    if (dateValidationError) {
      logger.error('❌ Event date validation failed during update', { id, date: eventData.date, error: dateValidationError });
      throw new Error(dateValidationError);
    }
    
    // Get Firestore reference
    const db = getFirestore();
    const eventRef = doc(db, EVENTS_COLLECTION, id);
    
    // Create a copy of the event data for updating
    const eventToUpdate = { ...eventData };
    
    // If a new image was provided, upload it and update the imageUrl
    if (imageFile) {
      logger.info('📸 Uploading new image for event update');
      const imageUrl = await uploadEventImage(imageFile);
      eventToUpdate.imageUrl = imageUrl;
    }
    
    // Update data with timestamp
    const updates = {
      ...eventToUpdate,
      updatedAt: serverTimestamp()
    };
    
    // Update the event in Firestore
    await updateDoc(eventRef, updates);
    
    // Return the updated event
    logger.success('✅ Event updated successfully', { id });
    return {
      id,
      ...eventToUpdate
    };
  } catch (error) {
    logger.error('❌ Error updating event:', error);
    
    // If the error comes from the image upload function, it will already have a descriptive message
    if (error instanceof Error) {
      throw error;
    }
    
    throw new Error('Failed to update event. Please check your connection and permissions.');
  }
};

/**
 * Delete an event from Firestore
 * @param id The ID of the event to delete
 */
export const deleteEvent = async (id: string): Promise<void> => {
  logger.info('🗑️ Deleting event from Firestore', { id });
  
  try {
    // Check if the current user is logged in and has admin rights
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to delete events');
    }
    
    // Check if user has admin role
    const isAdmin = await checkAdminClaims(currentUser);
    if (!isAdmin) {
      throw new Error('Only administrators can delete events');
    }
    
    const db = getFirestore();
    const eventRef = doc(db, EVENTS_COLLECTION, id);
    
    await deleteDoc(eventRef);
    
    logger.success('✅ Event deleted successfully', { id });
  } catch (error) {
    logger.error('❌ Error deleting event:', error);
    throw new Error('Failed to delete event');
  }
};

/**
 * Upload an event image to Firebase Storage
 * @param imageFile The image file to upload
 * @returns A promise that resolves to the download URL of the uploaded image
 */
const uploadEventImage = async (imageFile: File): Promise<string> => {
  logger.info('📸 Uploading event image to Firebase Storage', { fileName: imageFile.name });

  try {
    // Check if the current user is logged in and has admin rights
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error('You must be logged in to upload images');
    }

    // Check if user has admin role
    const isAdmin = await checkAdminClaims(currentUser);
    if (!isAdmin) {
      throw new Error('Only administrators can upload event images');
    }

    // Validate file type
    if (!imageFile.type.startsWith('image/')) {
      throw new Error('Invalid file type. Please upload an image file (JPEG, PNG, etc.)');
    }

    // Validate file size (max 5MB)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (imageFile.size > MAX_FILE_SIZE) {
      throw new Error('Image file is too large. Maximum allowed size is 5MB.');
    }

    // Create unique filename
    const fileName = `event-${Date.now()}-${imageFile.name.replace(/[^a-zA-Z0-9.]/g, '-')}`;

    // Get storage reference
    const storage = getStorage();
    const imageRef = ref(storage, `eventImages/${fileName}`);

    // Upload file
    await uploadBytes(imageRef, imageFile);
    logger.info('📸 Image uploaded successfully');

    // Get download URL
    const downloadURL = await getDownloadURL(imageRef);
    logger.success('✅ Image URL retrieved successfully');

    return downloadURL;
  } catch (error) {
    logger.error('❌ Error uploading event image:', error);

    // Handle specific Firebase Storage permission errors
    if (typeof error === 'object' && error && 'code' in error) {
      const firebaseError = error as { code: string, message: string };

      if (firebaseError.code === 'storage/unauthorized') {
        throw new Error("You don't have permission to upload images. Please make sure you have admin rights and are logged in.");
      }
      throw new Error(firebaseError.message || 'Failed to upload event image');
    }

    throw new Error(
      typeof error === 'object' && error && 'message' in error
        ? (error as Error).message
        : 'Failed to upload event image'
    );
  }
};

/**
 * Fetch a single event by its ID
 * @param eventId The ID of the event to fetch
 * @returns A promise that resolves to an EventItem object or null if not found
 */
export const getEventById = async (eventId: string): Promise<EventItem | null> => {
  logger.info('🔍 Fetching event by ID', { eventId });

  try {
    const db = getFirestore();
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    const eventSnapshot = await getDoc(eventRef);
    
    if (!eventSnapshot.exists()) {
      logger.warn('⚠️ Event not found', { eventId });
      return null;
    }
    
    const data = eventSnapshot.data();
    const event: EventItem = {
      id: eventSnapshot.id,
      title: data.title || '',
      date: data.date || '',
      imageUrl: data.imageUrl || '',
      address: data.address || '',
      description: data.description || '',
      startTime: {
        doorsOpen: data.startTime?.doorsOpen || '',
        doorsClose: data.startTime?.doorsClose || ''
      },
      endTime: data.endTime || ''
    };
    
    logger.success('✅ Event fetched successfully', { id: event.id });
    return event;
  } catch (error) {
    logger.error('❌ Error fetching event:', error);
    throw new Error('Failed to fetch event');
  }
};
